import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULES,
  type PasswordRule,
  validatePassword,
} from "@edm/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Circle, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { BrandMark } from "@/components/brand-mark";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { APP_NAME, APP_VERSION } from "@/config/app";
import { ApiError, getSetupStatus } from "@/lib/api";
import { useSetup } from "@/lib/auth";

const setupFormSchema = z
  .object({
    setupToken: z.string().min(1, "请输入初始化令牌"),
    displayName: z.string().trim().min(1, "请输入姓名").max(80),
    identifierType: z.enum(["phone", "email", "adid"]),
    identifierValue: z.string().trim().min(1, "请输入登录标识").max(200),
    password: z.string().min(PASSWORD_MIN_LENGTH, `密码至少 ${PASSWORD_MIN_LENGTH} 个字符`),
    confirmPassword: z.string().min(1, "请再次输入密码"),
  })
  .superRefine((value, ctx) => {
    const result = validatePassword(value.password);
    if (!result.ok) {
      ctx.addIssue({
        code: "custom",
        path: ["password"],
        message: `密码还需满足：${result.unmet.join("、")}`,
      });
    }
    if (value.password !== value.confirmPassword) {
      ctx.addIssue({ code: "custom", path: ["confirmPassword"], message: "两次输入的密码不一致" });
    }
  });

type SetupForm = z.infer<typeof setupFormSchema>;

function RuleList({ password }: { password: string }) {
  return (
    <ul className="grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
      {PASSWORD_RULES.map((rule: PasswordRule) => {
        const met = rule.test(password);
        return (
          <li
            key={rule.code}
            className={
              met ? "flex items-center gap-1.5 text-emerald-700" : "flex items-center gap-1.5"
            }
          >
            {met ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

export function SetupPage() {
  const navigate = useNavigate();
  const setup = useSetup();
  const [error, setError] = useState<string | null>(null);
  const status = useQuery({ queryKey: ["setup-status"], queryFn: getSetupStatus, retry: false });
  const form = useForm<SetupForm>({
    resolver: zodResolver(setupFormSchema),
    defaultValues: {
      setupToken: "",
      displayName: "",
      identifierType: "email",
      identifierValue: "",
      password: "",
      confirmPassword: "",
    },
  });
  const password = form.watch("password");

  useEffect(() => {
    if (status.data?.bootstrapped) {
      navigate({ to: "/login", replace: true });
    }
  }, [status.data?.bootstrapped, navigate]);

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const me = await setup.mutateAsync({
        setupToken: values.setupToken,
        displayName: values.displayName,
        identifierType: values.identifierType,
        identifierValue: values.identifierValue,
        password: values.password,
      });
      toast.success(`系统初始化完成，欢迎 ${me.user.displayName}`);
      navigate({ to: "/", replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "初始化失败，请稍后重试");
    }
  });

  return (
    <div className="min-h-screen bg-background px-4 py-10 sm:px-8">
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <BrandMark />
            <div>
              <p className="text-sm font-semibold">{APP_NAME}</p>
              <p className="text-xs text-muted-foreground">首次部署初始化</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-sidebar p-6 text-white">
            <ShieldCheck className="h-6 w-6 text-blue-300" />
            <h1 className="mt-4 text-2xl font-semibold">创建第一个系统管理员</h1>
            <p className="mt-2 text-sm leading-6 text-slate-300">
              初始化只允许执行一次。完成后，系统管理员可在“设置”中创建公司、部门、人员、
              角色与物料层级。
            </p>
            <div className="mt-6 space-y-3 text-sm text-slate-300">
              <p className="flex gap-2">
                <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" />
                初始化令牌来自 Worker Secret{" "}
                <code className="rounded bg-white/10 px-1">SETUP_TOKEN</code>。
              </p>
              <p className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" />
                密码使用 PBKDF2 + Pepper 保存；首次登录后仍建议定期更换。
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            版本 {APP_VERSION} · 初始化令牌不会写入浏览器存储。
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>系统初始化</CardTitle>
            <CardDescription>创建管理员账号后，即可进入工作台。</CardDescription>
          </CardHeader>
          <CardContent>
            {status.data?.bootstrapped ? (
              <Alert variant="warning" title="系统已经初始化">
                如需登录，请前往{" "}
                <Link to="/login" className="font-medium underline">
                  登录页
                </Link>
                。
              </Alert>
            ) : (
              <>
                {error ? (
                  <Alert variant="danger" className="mb-5" title="初始化未完成">
                    {error}
                  </Alert>
                ) : null}
                <form className="space-y-4" onSubmit={onSubmit} noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="setupToken">初始化令牌</Label>
                    <Input
                      id="setupToken"
                      placeholder="Worker Secret SETUP_TOKEN"
                      autoComplete="off"
                      {...form.register("setupToken")}
                    />
                    {form.formState.errors.setupToken ? (
                      <p className="text-xs text-red-600">
                        {form.formState.errors.setupToken.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="displayName">管理员姓名</Label>
                    <Input
                      id="displayName"
                      placeholder="例如：张管理"
                      {...form.register("displayName")}
                    />
                    {form.formState.errors.displayName ? (
                      <p className="text-xs text-red-600">
                        {form.formState.errors.displayName.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
                    <div className="space-y-2">
                      <Label htmlFor="identifierType">登录标识类型</Label>
                      <select
                        id="identifierType"
                        className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        {...form.register("identifierType")}
                      >
                        <option value="email">邮箱</option>
                        <option value="phone">手机号</option>
                        <option value="adid">ADID</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="identifierValue">登录标识</Label>
                      <Input
                        id="identifierValue"
                        placeholder="admin@example.com"
                        {...form.register("identifierValue")}
                      />
                      {form.formState.errors.identifierValue ? (
                        <p className="text-xs text-red-600">
                          {form.formState.errors.identifierValue.message}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">密码</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      {...form.register("password")}
                    />
                    <RuleList password={password} />
                    {form.formState.errors.password ? (
                      <p className="text-xs text-red-600">
                        {form.formState.errors.password.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">确认密码</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      {...form.register("confirmPassword")}
                    />
                    {form.formState.errors.confirmPassword ? (
                      <p className="text-xs text-red-600">
                        {form.formState.errors.confirmPassword.message}
                      </p>
                    ) : null}
                  </div>

                  <Button type="submit" className="w-full" disabled={setup.isPending}>
                    {setup.isPending ? "正在初始化…" : "创建管理员并进入系统"}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
